import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { notification } from 'antd';

import AmazonWebServicesContext from '@totem/components/administration/amazonWebServices/AmazonWebServicesContext';
import {
  AwsHierarchyCostReport,
  AwsInstanceAction,
  AwsInstanceRecord,
  AwsInstanceRecords,
  AwsMonthlyCosts,
  AwsStatusCostReport,
  AwsSummary,
} from '@totem/components/administration/amazonWebServices/types';
import { getToken } from '@totem/components/userContext/accountUtilities';
import {
  AWS_HIERARCHY_COST_ENDPOINT,
  AWS_INSTANCES_ENDPOINT,
  AWS_KPI_ENDPOINT,
  AWS_MONTHLY_COSTS_ENDPOINT,
  AWS_STATUS_COST_ENDPOINT,
} from '@totem/utilities/endpoints';

const EMPTY_SUMMARY: AwsSummary = {
  totalInstances: 0,
  currentMonthCost: '0',
  previousMonthCost: '0',
  currency: 'USD',
};

const EMPTY_HIERARCHY_COST_REPORT: AwsHierarchyCostReport = {
  months: [],
  currency: 'USD',
  organizations: [],
};

const EMPTY_STATUS_COST_REPORT: AwsStatusCostReport = {
  months: [],
  currency: 'USD',
  statuses: [],
};

type Props = {
  children: ReactNode;
};

const AmazonWebServicesContainer = ({ children }: Props) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AwsInstanceRecords>([]);
  const [summary, setSummary] = useState<AwsSummary>(EMPTY_SUMMARY);
  const [monthlyCosts, setMonthlyCosts] = useState<AwsMonthlyCosts>([]);
  const [hierarchyCostReport, setHierarchyCostReport] =
    useState<AwsHierarchyCostReport>(EMPTY_HIERARCHY_COST_REPORT);
  const [statusCostReport, setStatusCostReport] = useState<AwsStatusCostReport>(
    EMPTY_STATUS_COST_REPORT,
  );
  const [selectedInstance, setSelectedInstance] =
    useState<AwsInstanceRecord | null>(null);
  const [activeAction, setActiveAction] = useState<AwsInstanceAction | null>(
    null,
  );

  const refresh = useCallback(() => {
    setLoading(true);

    const headers = { Authorization: `Bearer ${getToken()}` };

    Promise.all([
      fetch(AWS_INSTANCES_ENDPOINT, { headers }).then((res) => res.json()),
      fetch(AWS_KPI_ENDPOINT, { headers }).then((res) => res.json()),
      fetch(AWS_MONTHLY_COSTS_ENDPOINT, { headers }).then((res) => res.json()),
      fetch(AWS_HIERARCHY_COST_ENDPOINT, { headers }).then((res) => res.json()),
      fetch(AWS_STATUS_COST_ENDPOINT, { headers }).then((res) => res.json()),
    ])
      .then(([instances, kpiSummary, monthly, hierarchyCosts, statusCosts]) => {
        setData(Array.isArray(instances) ? instances : []);

        setSummary(kpiSummary ?? EMPTY_SUMMARY);

        setMonthlyCosts(Array.isArray(monthly) ? monthly : []);

        setHierarchyCostReport(hierarchyCosts ?? EMPTY_HIERARCHY_COST_REPORT);

        setStatusCostReport(statusCosts ?? EMPTY_STATUS_COST_REPORT);
      })
      .catch(() =>
        notification.error({ message: 'Failed to load AWS instances.' }),
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onAction = (action: AwsInstanceAction, instance: AwsInstanceRecord) => {
    setSelectedInstance(instance);

    setActiveAction(action);
  };

  const onActionClose = () => {
    setSelectedInstance(null);

    setActiveAction(null);
  };

  return (
    <AmazonWebServicesContext.Provider
      value={{
        loading,
        data,
        summary,
        monthlyCosts,
        hierarchyCostReport,
        statusCostReport,
        onRefresh: refresh,
        selectedInstance,
        activeAction,
        onAction,
        onActionClose,
      }}
    >
      {children}
    </AmazonWebServicesContext.Provider>
  );
};

export default AmazonWebServicesContainer;
